import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Calendar, Clock, MapPin, Phone, Car, Truck, Wrench, 
  CheckCircle, AlertCircle, ChevronRight, Star, CircleDot
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { PageSeo } from '@/components/seo/PageSeo';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { format, addDays } from 'date-fns';
import { tr, enUS, de, fr, es, it, pt, ru, arSA, zhCN } from 'date-fns/locale';
import { buildPathForLocale } from '@/lib/intl-seo';

const DATE_FNS_LOCALES = { tr, en: enUS, de, fr, es, it, pt, ru, ar: arSA, zh: zhCN };

const TIRE_COUNTS = [1, 2, 4, 6, 8];

const TireAppointmentPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const lc = (i18n.language || 'en').substring(0, 2);
  const dateLocale = DATE_FNS_LOCALES[lc] || enUS;

  const vehicleTypes = useMemo(() => [
    { value: 'car', label: t('tireAppointment.vehicle.car'), icon: Car },
    { value: 'truck', label: t('tireAppointment.vehicle.truck'), icon: Truck },
    { value: 'tir', label: t('tireAppointment.vehicle.tir'), icon: Truck },
    { value: 'tractor', label: t('tireAppointment.vehicle.tractor'), icon: CircleDot },
  ], [t]);

  const services = useMemo(() => [
    { value: 'tire_change', label: t('tireAppointment.service.tireChange'), description: t('tireAppointment.service.tireChangeDesc') },
    { value: 'tire_balance', label: t('tireAppointment.service.tireBalance'), description: t('tireAppointment.service.tireBalanceDesc') },
    { value: 'tire_alignment', label: t('tireAppointment.service.tireAlignment'), description: t('tireAppointment.service.tireAlignmentDesc') },
  ], [t]);
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [serviceCenters, setServiceCenters] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  
  const [formData, setFormData] = useState({
    vehicle_type: 'car',
    vehicle_brand: '',
    vehicle_model: '',
    vehicle_plate: '',
    tire_count: 4,
    tire_size: searchParams.get('tire_size') || '',
    services: ['tire_change'],
    customer_name: user?.name || '',
    customer_phone: user?.phone || '',
    customer_email: user?.email || '',
    customer_notes: ''
  });

  useEffect(() => {
    fetchServiceCenters();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || '',
        customer_phone: user.phone || '',
        customer_email: user.email || ''
      }));
    }
  }, [user]);

  useEffect(() => {
    if (selectedCenter && selectedDate) {
      fetchAvailability();
    }
  }, [selectedCenter, selectedDate]);

  const fetchServiceCenters = async () => {
    try {
      const res = await api.get('/tire-appointments/service-centers');
      setServiceCenters(res.data);
    } catch (error) {
      console.error('Service centers fetch error:', error);
      toast.error(t('tireAppointment.centersLoadFailed'));
    }
  };

  const fetchAvailability = async () => {
    if (!selectedCenter || !selectedDate) return;
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const res = await api.get(`/tire-appointments/service-centers/${selectedCenter.id}/availability?date=${dateStr}`);
      setAvailability(res.data);
      setSelectedTime('');
    } catch (error) {
      console.error('Availability fetch error:', error);
    }
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => {
      const nextServices = prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service];
      if (!nextServices.includes('tire_change')) {
        nextServices.unshift('tire_change');
      }
      return { ...prev, services: nextServices };
    });
  };

  const calculateEstimatedPrice = () => {
    if (!selectedCenter) return 0;
    const priceList = selectedCenter.price_list || {};
    let price = 0;
    const vehicleType = formData.vehicle_type;
    
    if (['car', 'araba', 'binek'].includes(vehicleType)) {
      price = (priceList.car_tire_change || 100) * (formData.tire_count / 4);
    } else {
      price = (priceList.truck_tire_change || 250) * (formData.tire_count / 4);
    }
    
    if (formData.services.includes('tire_balance')) {
      price += priceList.tire_balance || 50;
    }
    if (formData.services.includes('tire_alignment')) {
      price += priceList.tire_alignment || 150;
    }
    
    return price;
  };

  const loginPath = `${buildPathForLocale(lc, ['login'])}?redirect=${encodeURIComponent(buildPathForLocale(lc, ['tire-appointment']))}`;
  const myAppointmentsPath = buildPathForLocale(lc, ['my-appointments']);

  const handleSubmit = async () => {
    if (!user) {
      toast.error(t('tireAppointment.loginRequired'));
      navigate(loginPath);
      return;
    }

    if (!selectedCenter || !selectedDate || !selectedTime) {
      toast.error(t('tireAppointment.selectCenterDateTime'));
      return;
    }

    if (!formData.customer_name || !formData.customer_phone) {
      toast.error(t('tireAppointment.fillContact'));
      return;
    }

    try {
      setLoading(true);
      const appointmentData = {
        service_center_id: selectedCenter.id,
        appointment_date: format(selectedDate, 'yyyy-MM-dd'),
        appointment_time: selectedTime,
        ...formData
      };

      await api.post('/tire-appointments', appointmentData);
      toast.success(t('tireAppointment.createSuccess'));
      navigate(myAppointmentsPath);
    } catch (error) {
      console.error('Appointment create error:', error);
      toast.error(error.response?.data?.detail || t('tireAppointment.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getServiceLabel = (value) => services.find(s => s.value === value)?.label || value;

  return (
    <div className="min-h-screen bg-background" data-testid="tire-appointment-page">
      <PageSeo title={t('tireAppointment.title')} description={t('tireAppointment.subtitle')} segments={['tire-appointment']} />
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            <Wrench className="inline-block w-8 h-8 mr-2 text-primary" />
            {t('tireAppointment.title')}
          </h1>
          <p className="text-muted-foreground">{t('tireAppointment.subtitle')}</p>
        </div>

        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-4">
            {[1, 2, 3, 4].map((s) => (
              <React.Fragment key={s}>
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                    step >= s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
                {s < 4 && <ChevronRight className="w-5 h-5 text-muted-foreground" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          {step === 1 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  {t('tireAppointment.step1Title')}
                </CardTitle>
                <CardDescription>{t('tireAppointment.step1Desc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {serviceCenters.map((center) => (
                    <Card 
                      key={center.id}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        selectedCenter?.id === center.id ? 'ring-2 ring-primary' : ''
                      }`}
                      onClick={() => setSelectedCenter(center)}
                      data-testid={`service-center-${center.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
                            <Wrench className="w-8 h-8 text-primary" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold">{center.name}</h3>
                            <p className="text-sm text-muted-foreground">{center.city} - {center.district}</p>
                            <p className="text-xs text-muted-foreground mt-1">{center.address}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                              <span className="text-sm font-medium">{center.rating}</span>
                              <span className="text-xs text-muted-foreground">
                                ({t('tireAppointment.reviews', { count: center.review_count })})
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                <div className="flex justify-end mt-6">
                  <Button onClick={() => setStep(2)} disabled={!selectedCenter} data-testid="next-step-1">
                    {t('tireAppointment.continue')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {step === 2 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  {t('tireAppointment.step2Title')}
                </CardTitle>
                <CardDescription>
                  {t('tireAppointment.step2Desc', { name: selectedCenter?.name })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="mb-2 block">{t('tireAppointment.selectDate')}</Label>
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      locale={dateLocale}
                      disabled={(date) => date < new Date() || date > addDays(new Date(), 30)}
                      className="rounded-md border"
                    />
                  </div>

                  <div>
                    <Label className="mb-2 block">{t('tireAppointment.selectTime')}</Label>
                    {!selectedDate ? (
                      <p className="text-muted-foreground text-sm">{t('tireAppointment.selectDateFirst')}</p>
                    ) : availability?.available_slots?.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {availability.available_slots.map((slot) => (
                          <Button
                            key={slot.time}
                            variant={selectedTime === slot.time ? "default" : "outline"}
                            className="h-12"
                            onClick={() => setSelectedTime(slot.time)}
                            data-testid={`time-slot-${slot.time}`}
                          >
                            <Clock className="w-4 h-4 mr-1" />
                            {slot.time}
                            <Badge variant="secondary" className="ml-1 text-xs">
                              {slot.available_spots}
                            </Badge>
                          </Button>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 bg-yellow-50 rounded-lg text-yellow-700 text-sm">
                        <AlertCircle className="w-4 h-4 inline mr-2" />
                        {availability?.message || t('tireAppointment.noSlotsAvailable')}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-between mt-6">
                  <Button variant="outline" onClick={() => setStep(1)}>{t('tireAppointment.back')}</Button>
                  <Button onClick={() => setStep(3)} disabled={!selectedDate || !selectedTime} data-testid="next-step-2">
                    {t('tireAppointment.continue')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {step === 3 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Car className="w-5 h-5 text-primary" />
                  {t('tireAppointment.step3Title')}
                </CardTitle>
                <CardDescription>{t('tireAppointment.step3Desc')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="mb-3 block">{t('tireAppointment.vehicleType')}</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {vehicleTypes.map(({ value, label, icon: Icon }) => (
                      <Button
                        key={value}
                        variant={formData.vehicle_type === value ? "default" : "outline"}
                        className="h-16 flex-col gap-1"
                        onClick={() => setFormData(prev => ({ ...prev, vehicle_type: value }))}
                        data-testid={`vehicle-type-${value}`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="text-xs">{label}</span>
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>{t('tireAppointment.vehicleBrand')}</Label>
                    <Input
                      placeholder={t('tireAppointment.vehicleBrandPlaceholder')}
                      value={formData.vehicle_brand}
                      onChange={(e) => setFormData(prev => ({ ...prev, vehicle_brand: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>{t('tireAppointment.vehicleModel')}</Label>
                    <Input
                      placeholder={t('tireAppointment.vehicleModelPlaceholder')}
                      value={formData.vehicle_model}
                      onChange={(e) => setFormData(prev => ({ ...prev, vehicle_model: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>{t('tireAppointment.plate')}</Label>
                    <Input
                      placeholder={t('tireAppointment.platePlaceholder')}
                      value={formData.vehicle_plate}
                      onChange={(e) => setFormData(prev => ({ ...prev, vehicle_plate: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('tireAppointment.tireSize')}</Label>
                    <Input
                      placeholder={t('tireAppointment.tireSizePlaceholder')}
                      value={formData.tire_size}
                      onChange={(e) => setFormData(prev => ({ ...prev, tire_size: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>{t('tireAppointment.tireCount')}</Label>
                    <Select 
                      value={String(formData.tire_count)}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, tire_count: parseInt(v) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIRE_COUNTS.map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            {t('tireAppointment.tireCountUnit', { count: n })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="mb-3 block">{t('tireAppointment.servicesLabel')}</Label>
                  <div className="space-y-3">
                    {services.map(({ value, label, description }) => (
                      <div 
                        key={value}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          formData.services.includes(value) ? 'bg-primary/5 border-primary' : 'hover:bg-muted/50'
                        }`}
                        onClick={() => value !== 'tire_change' && handleServiceToggle(value)}
                      >
                        <Checkbox
                          checked={formData.services.includes(value)}
                          disabled={value === 'tire_change'}
                          onCheckedChange={() => handleServiceToggle(value)}
                        />
                        <div className="flex-1">
                          <p className="font-medium">{label}</p>
                          <p className="text-sm text-muted-foreground">{description}</p>
                        </div>
                        <Badge variant="outline">
                          {selectedCenter?.price_list?.[value === 'tire_change' 
                            ? (formData.vehicle_type === 'car' ? 'car_tire_change' : 'truck_tire_change')
                            : value] || 0} ₺
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-between mt-6">
                  <Button variant="outline" onClick={() => setStep(2)}>{t('tireAppointment.back')}</Button>
                  <Button onClick={() => setStep(4)} data-testid="next-step-3">
                    {t('tireAppointment.continue')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {step === 4 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-primary" />
                  {t('tireAppointment.step4Title')}
                </CardTitle>
                <CardDescription>{t('tireAppointment.step4Desc')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('tireAppointment.contactName')}</Label>
                    <Input
                      value={formData.customer_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('tireAppointment.contactPhone')}</Label>
                    <Input
                      value={formData.customer_phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                      placeholder={t('tireAppointment.phonePlaceholder')}
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>{t('tireAppointment.contactEmail')}</Label>
                    <Input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>{t('tireAppointment.notes')}</Label>
                    <Textarea
                      value={formData.customer_notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_notes: e.target.value }))}
                      placeholder={t('tireAppointment.notesPlaceholder')}
                    />
                  </div>
                </div>

                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold">{t('tireAppointment.summaryTitle')}</h4>
                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">{t('tireAppointment.serviceCenter')}</span>
                      <p className="font-medium">{selectedCenter?.name}</p>
                      <p className="text-xs text-muted-foreground">{selectedCenter?.address}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('tireAppointment.dateTime')}</span>
                      <p className="font-medium">
                        {selectedDate && format(selectedDate, 'd MMMM yyyy', { locale: dateLocale })}
                        {' - '}{selectedTime}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('tireAppointment.vehicleLabel')}</span>
                      <p className="font-medium">
                        {vehicleTypes.find(v => v.value === formData.vehicle_type)?.label}
                        {formData.vehicle_brand && ` - ${formData.vehicle_brand}`}
                        {formData.vehicle_model && ` ${formData.vehicle_model}`}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('tireAppointment.servicesSummary')}</span>
                      <p className="font-medium">
                        {formData.services.map(getServiceLabel).join(', ')}
                      </p>
                    </div>
                  </div>
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">{t('tireAppointment.estimatedPrice')}</span>
                      <span className="text-2xl font-bold text-primary">{calculateEstimatedPrice()} ₺</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{t('tireAppointment.priceNote')}</p>
                  </div>
                </div>

                <div className="flex justify-between mt-6">
                  <Button variant="outline" onClick={() => setStep(3)}>{t('tireAppointment.back')}</Button>
                  <Button 
                    onClick={handleSubmit} 
                    disabled={loading || !formData.customer_name || !formData.customer_phone}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="confirm-appointment"
                  >
                    {loading ? t('tireAppointment.processing') : t('tireAppointment.confirmAppointment')}
                    <CheckCircle className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default TireAppointmentPage;
